import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import FormContainer from "../../containers/FormContainer";
import TextBox from "../shared/TextBox";
import SelectBox from "../shared/SelectBox";
import SubmitButton from "../shared/SubmitButton";
import { useModal } from "../modal/ModalContext";
import { countryOptions } from "../../constants";
import {
  createLinkedInAccount,
  updateLinkedInAccount,
  connectLinkedInAccount,
} from "../../services/apiService";
import {
  unwrapAccountPayload,
  extractApiErrorMessage,
  waitForProxyReady,
} from "./proxyReady";

const proxyTypeOptions = [
  { value: "RESIDENTIAL", label: "RESIDENTIAL" },
  { value: "DATACENTER", label: "DATACENTER" },
  { value: "MOBILE", label: "MOBILE" },
];

const unwrapConnectResult = (response) => {
  const body = response?.data;
  if (!body) return {};
  if (body.status || body.suggestedAction || body.error) return body;
  if (body.data && (body.data.status || body.data.suggestedAction)) return body.data;
  return body;
};

/**
 * Slim HeyReach-style connect/reconnect wizard: email + password + country (+ optional custom proxy).
 * Creates or updates credentials, waits for proxy READY, then connects.
 */
const ConnectLinkedInWizard = ({ account, onSuccess, onConnectStarted }) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();
  const isReconnect = !!account;
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [showProxy, setShowProxy] = useState(false);
  const [form, setForm] = useState({
    email: account?.email || "",
    password: "",
    proxyCountry: (account?.proxyCountry || "").toUpperCase(),
    proxyHost: account?.proxyHost || "",
    proxyPort: account?.proxyPort || "",
    proxyUsername: account?.proxyUsername || "",
    proxyPassword: "",
    proxyType: account?.proxyType || "RESIDENTIAL",
  });

  const translatedCountries = countryOptions.map((opt) => ({
    value: opt.value,
    label: t(opt.label),
  }));

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const isDisabled = !form.email || !form.password || !form.proxyCountry;

  const buildProxyPayload = () => {
    const payload = {
      proxyCountry: form.proxyCountry ? form.proxyCountry.toLowerCase() : undefined,
    };
    if (!showProxy || !form.proxyHost) return payload;
    payload.proxyHost = form.proxyHost;
    payload.proxyUsername = form.proxyUsername || undefined;
    payload.proxyPassword = form.proxyPassword || undefined;
    payload.proxyType = form.proxyType || "RESIDENTIAL";
    if (form.proxyPort) payload.proxyPort = parseInt(form.proxyPort, 10);
    return payload;
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setFormError(null);
    try {
      let accountId = account?.id;
      const proxyPayload = buildProxyPayload();
      if (isReconnect) {
        const updatePayload = {
          password: form.password,
          ...proxyPayload,
        };
        Object.keys(updatePayload).forEach((key) => {
          if (updatePayload[key] === "" || updatePayload[key] === undefined) {
            delete updatePayload[key];
          }
        });
        await updateLinkedInAccount(accountId, updatePayload);
      } else {
        const createPayload = {
          accountName: form.email,
          email: form.email,
          password: form.password,
          ...proxyPayload,
        };
        Object.keys(createPayload).forEach((key) => {
          if (createPayload[key] === "" || createPayload[key] === undefined) {
            delete createPayload[key];
          }
        });
        const createResponse = await createLinkedInAccount(createPayload);
        const created = unwrapAccountPayload(createResponse);
        accountId = created?.id;
        if (!accountId) {
          throw new Error("Account created but id missing from response");
        }
      }
      hideModal();
      if (onConnectStarted) {
        onConnectStarted({
          id: accountId,
          email: form.email,
          accountName: account?.accountName || form.email,
          status: "CONNECTING",
          isConnected: false,
          proxyProvisioningStatus: "PROVISIONING",
        });
      }
      try {
        await waitForProxyReady(accountId);
        const connectResponse = await connectLinkedInAccount(accountId);
        const result = unwrapConnectResult(connectResponse);
        if (onSuccess) onSuccess(result, accountId);
      } catch (connectErr) {
        const msg = extractApiErrorMessage(connectErr, t("linkedin.toast.error"));
        // onSuccess reports the failure itself; toasting here too showed it twice.
        if (!onSuccess) toast.error(typeof msg === "string" ? msg : t("linkedin.toast.error"));
        if (onSuccess) {
          onSuccess(
            {
              success: false,
              httpStatus: connectErr?.status ?? connectErr?.response?.status,
              status: connectErr?.response?.data?.status || "ERROR",
              suggestedAction: connectErr?.response?.data?.suggestedAction
                || connectErr?.response?.data?.error?.suggestedAction,
              error: {
                message: msg,
                suggestedAction: connectErr?.response?.data?.suggestedAction
                  || connectErr?.response?.data?.error?.suggestedAction,
              },
            },
            accountId
          );
        }
      }
    } catch (err) {
      const msg = extractApiErrorMessage(err, t("linkedin.toast.error"));
      setFormError(Array.isArray(msg) ? msg : msg);
      toast.error(typeof msg === "string" ? msg : t("linkedin.toast.error"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-[40px]">
      <p className="text-gray-500 text-sm mb-5">
        {isReconnect ? t("linkedin.reconnectSubhead") : t("linkedin.connectSubhead")}
      </p>
      <FormContainer
        isLoading={isLoading}
        error={typeof formError === "string" || Array.isArray(formError) ? formError : null}
        onSubmit={handleSubmit}
      >
        <TextBox
          label={t("linkedin.email")}
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          placeholder={t("linkedin.email_placeholder")}
          error={formError?.email}
          disabled={isReconnect}
        />
        <TextBox
          label={t("linkedin.password")}
          name="password"
          type="password"
          value={form.password}
          onChange={handleChange}
          placeholder={t("linkedin.password_placeholder")}
          error={formError?.password}
        />
        <SelectBox
          label={t("linkedin.selectCountry")}
          name="proxyCountry"
          value={form.proxyCountry}
          onChange={handleChange}
          options={translatedCountries}
          placeholder={t("linkedin.selectCountry_placeholder")}
        />
        <p className="text-xs text-gray-500 -mt-3 mb-4">{t("linkedin.selectCountryHint")}</p>
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowProxy(!showProxy)}
            className="flex items-center gap-2 text-[14px] font-bold text-gray-700 hover:underline focus:outline-none"
          >
            <span>{showProxy ? "▼" : "▶"}</span>
            {t("linkedin.addOwnProxy")}
          </button>
        </div>
        {showProxy && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 border rounded-[8px] p-4 bg-gray-50 mb-4">
            <TextBox
              label={t("linkedin.proxyHost")}
              name="proxyHost"
              value={form.proxyHost}
              onChange={handleChange}
              placeholder={t("linkedin.proxyHost_placeholder")}
            />
            <TextBox
              label={t("linkedin.proxyPort")}
              name="proxyPort"
              type="number"
              value={form.proxyPort}
              onChange={handleChange}
              placeholder={t("linkedin.proxyPort_placeholder")}
            />
            <TextBox
              label={t("linkedin.proxyUsername")}
              name="proxyUsername"
              value={form.proxyUsername}
              onChange={handleChange}
              placeholder={t("linkedin.proxyUsername_placeholder")}
            />
            <TextBox
              label={t("linkedin.proxyPassword")}
              name="proxyPassword"
              type="password"
              value={form.proxyPassword}
              onChange={handleChange}
              placeholder={t("linkedin.proxyPassword_placeholder")}
            />
            <SelectBox
              label={t("linkedin.proxyType")}
              name="proxyType"
              value={form.proxyType}
              onChange={handleChange}
              options={proxyTypeOptions}
            />
          </div>
        )}
        <SubmitButton
          customStyle="inline"
          label={isReconnect ? t("linkedin.reconnect") : t("linkedin.connectAccount")}
          disabled={isDisabled}
          isLoading={isLoading}
        />
      </FormContainer>
    </div>
  );
};

export default ConnectLinkedInWizard;
export { unwrapConnectResult };
